import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { unlockSeats } from '@/actions/purchase';
import useWhitelabelEnvs from './whitelabel/useWhitelabelEnvs';

/**
 * Routes that hold locked seats, where we must NOT release them.
 *
 * `secure/cielo` is the Cielo payment webview: it's reached by loading the URL fresh
 * (the funnel never navigates to it internally), so on a desktop browser a reload or
 * tab-close fires `beforeunload` and would beacon `unlock_seats` mid-payment — releasing
 * the seats and dropping the ticket meta the form prefills from. It belongs alongside the
 * other payment surfaces. (Inside the RN WebView this never fired because native teardown
 * doesn't deliver `beforeunload`, which is why it stayed hidden.)
 *
 * @param {string} pathname - Current location pathname.
 * @returns {boolean} True when the route holds locked seats and must be skipped.
 */
const holdsLockedSeats = (pathname) =>
  pathname.includes('purchase') ||
  pathname.includes('payment') ||
  pathname.includes('secure/cielo') ||
  pathname.includes('sso/login') ||
  pathname.includes('sso/logout');

const useUnlockSeats = () => {
  const location = useLocation();
  const { api } = useWhitelabelEnvs();
  const purchase = useSelector(({ purchase }) => purchase.toJS());
  const payment = useSelector(({ payment }) => payment.toJS());

  // Event that is fired on beforeunload
  // This sends a beacon to purchase api for unlock seats on current token
  const beforeUnloadCallback = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('quickPurchaseToken')) return;
    if (holdsLockedSeats(location.pathname)) {
      return;
    }
    navigator.sendBeacon(`${api.purchaseUrl}/v2/purchases/${purchase.token}/unlock_seats`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchase.token]);

  useEffect(() => {
    // Fire event when closing tab or browser
    if (
      window &&
      purchase.loaded &&
      purchase.token &&
      purchase.passengers?.length > 0 &&
      purchase.state !== 'completed' &&
      purchase.state !== 'pending' &&
      payment.status !== 'pending'
    ) {
      // Remove previous event before create a new one
      window.removeEventListener('beforeunload', beforeUnloadCallback);
      // Create event with beacon call to unlock seats
      window.addEventListener('beforeunload', beforeUnloadCallback, { once: true });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    purchase.token,
    purchase.state,
    purchase.passengers?.length,
    purchase.loaded,
    payment.status,
  ]);

  // Remove event listener if purchase waiting for payment or complete
  useEffect(() => {
    if (
      window &&
      purchase.token &&
      (purchase.state === 'completed' ||
        purchase.state === 'pending' ||
        payment.status === 'pending' ||
        payment.status === 'attempt')
    ) {
      window.removeEventListener('beforeunload', beforeUnloadCallback);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchase.state, payment.status]);

  useEffect(() => {
    if (window && !holdsLockedSeats(location.pathname)) {
      // Unlock seats of the last purchase
      const lastStoredToken = window.sessionStorage?.getItem('lastPurchaseToken');
      if (lastStoredToken) {
        unlockSeats({ purchaseToken: lastStoredToken });
        window.sessionStorage?.removeItem('lastPurchaseToken');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);
};

export default useUnlockSeats;
